/**
 * K2VsK3Page - Comparison guide between K2 and K3
 *
 * SEO Target: /arsredovisning/guider/k2-vs-k3
 * Primary keywords: K2 vs K3, K2 eller K3, skillnad K2 K3, välja regelverk
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import {
  Check,
  X,
  ArrowRight,
  FileText,
  Calculator,
  Building2,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  ChevronRight,
} from "lucide-react";

export function K2VsK3Page() {
  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Guider", href: "/arsredovisning/guider" },
    { label: "K2 vs K3" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Guider", url: "/arsredovisning/guider" },
    { name: "K2 vs K3", url: "/arsredovisning/guider/k2-vs-k3" },
  ]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "K2 vs K3 - Vilket regelverk ska du välja?",
    description:
      "Komplett jämförelse mellan K2 och K3 regelverk. Lär dig skillnaderna och välj rätt för ditt företag.",
    author: {
      "@type": "Organization",
      name: "Kvanten",
    },
    publisher: {
      "@type": "Organization",
      name: "Kvanten",
      url: SEO_CONFIG.siteUrl,
    },
    datePublished: "2025-01-15",
    dateModified: "2026-02-01",
  };

  const comparisonData = [
    {
      area: "Regelverk",
      k2: "BFNAR 2016:10",
      k3: "BFNAR 2012:1",
    },
    {
      area: "Målgrupp",
      k2: "Mindre företag",
      k3: "Alla företag",
    },
    {
      area: "Antal noter",
      k2: "~15 stycken",
      k3: "~25-30 stycken",
    },
    {
      area: "Kassaflödesanalys",
      k2: "Frivillig",
      k3: "Obligatorisk",
      k2Highlight: "green",
      k3Highlight: "neutral",
    },
    {
      area: "Uppskjuten skatt",
      k2: "Ej redovisas",
      k3: "Ska redovisas",
      k2Highlight: "green",
      k3Highlight: "neutral",
    },
    {
      area: "Värdering",
      k2: "Anskaffningsvärde",
      k3: "Anskaffnings- eller verkligt värde",
    },
    {
      area: "Avskrivning",
      k2: "Schablon (5 år default)",
      k3: "Komponentavskrivning",
    },
    {
      area: "Jämförelsetal i noter",
      k2: "Frivilligt",
      k3: "Obligatoriskt",
      k2Highlight: "green",
      k3Highlight: "neutral",
    },
    {
      area: "Koncernredovisning",
      k2: "Ej tillåtet",
      k3: "Tillåtet",
      k2Highlight: "red",
      k3Highlight: "green",
    },
  ];

  return (
    <>
      <PageMeta
        title="K2 vs K3 - Vilket Regelverk Ska Du Välja? | Komplett Guide | Kvanten"
        description="Jämför K2 och K3 regelverk för årsredovisning. Förstå skillnaderna i noter, värdering och rapportering. Hjälp att välja rätt för ditt företag."
        keywords="K2 vs K3, K2 eller K3, skillnad K2 K3, välja regelverk årsredovisning, BFNAR 2016:10, BFNAR 2012:1"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/guider/k2-vs-k3`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-6">
                <FileText className="w-4 h-4" />
                Guide • 12 min läsning
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                K2 vs K3: Vilket regelverk passar{" "}
                <span className="text-indigo-600">ditt företag?</span>
              </h1>

              <p className="text-xl text-gray-600 mb-8">
                Alla svenska aktiebolag måste välja mellan K2 och K3. Den här guiden hjälper dig
                förstå skillnaderna och välja rätt.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/wizard"
                  className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
                >
                  Skapa årsredovisning
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="#jamforelse"
                  className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Se jämförelsetabell
                </a>
              </div>
            </div>

            {/* Quick answer card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Snabbt svar</h2>
              </div>
              <p className="text-gray-600 mb-6">
                <strong className="text-gray-900">De flesta småföretag bör välja K2.</strong> Det är
                enklare, har färre noter och tillåter schablonmässig värdering.
              </p>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600">
                  <strong>Välj K3 endast om du:</strong>
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-indigo-500" />
                    Behöver koncernredovisning
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-indigo-500" />
                    Vill använda verkligt värde-värdering
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-indigo-500" />
                    Har speciella intressentkrav
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview cards */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Översikt K2 och K3</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Förstå grunderna i de två regelverken innan du gör ditt val
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* K2 Card */}
            <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
                  Förenklat regelverk
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">K2</h3>
                <p className="text-gray-600 mb-6">
                  <strong>BFNAR 2016:10</strong> — Det förenklade regelverket för mindre företag.
                  Passar över 95% av alla svenska aktiebolag.
                </p>
                <ul className="space-y-3">
                  {[
                    "Enklare regler och färre val",
                    "Cirka 15 noter",
                    "Schablonmässig avskrivning",
                    "Ingen kassaflödesanalys krävs",
                    "Ingen uppskjuten skatt",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/arsredovisning/k2"
                  className="inline-flex items-center gap-2 mt-6 text-green-700 font-medium hover:text-green-800"
                >
                  Läs mer om K2
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* K3 Card */}
            <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                  Huvudregelverket
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">K3</h3>
                <p className="text-gray-600 mb-6">
                  <strong>BFNAR 2012:1</strong> — Huvudregelverket baserat på IFRS for SMEs. För
                  större företag eller frivilligt val.
                </p>
                <ul className="space-y-3">
                  {[
                    "Principbaserade regler",
                    "Cirka 25-30 noter",
                    "Komponentavskrivning",
                    "Kassaflödesanalys obligatorisk",
                    "Uppskjuten skatt redovisas",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/arsredovisning/k3"
                  className="inline-flex items-center gap-2 mt-6 text-blue-700 font-medium hover:text-blue-800"
                >
                  Läs mer om K3
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section id="jamforelse" className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Detaljerad jämförelse</h2>
            <p className="text-xl text-gray-600">Skillnaderna mellan K2 och K3 på ett ögonblick</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left py-4 px-6 bg-gray-100 font-semibold text-gray-900">
                      Område
                    </th>
                    <th className="text-left py-4 px-6 bg-green-50 font-semibold text-green-900">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-500 rounded-full" />
                        K2
                      </div>
                    </th>
                    <th className="text-left py-4 px-6 bg-blue-50 font-semibold text-blue-900">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-blue-500 rounded-full" />
                        K3
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {comparisonData.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-medium text-gray-900">{row.area}</td>
                      <td className="py-4 px-6 bg-green-50/50">
                        <span
                          className={
                            row.k2Highlight === "green"
                              ? "text-green-700 font-medium"
                              : row.k2Highlight === "red"
                                ? "text-red-600 font-medium"
                                : "text-gray-700"
                          }
                        >
                          {row.k2}
                        </span>
                      </td>
                      <td className="py-4 px-6 bg-blue-50/50">
                        <span
                          className={
                            row.k3Highlight === "green"
                              ? "text-green-700 font-medium"
                              : row.k3Highlight === "red"
                                ? "text-red-600 font-medium"
                                : "text-gray-700"
                          }
                        >
                          {row.k3}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Who can use K2 */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Vem får använda K2?</h2>
              </div>

              <p className="text-gray-600 mb-6">
                K2 får användas av <strong>mindre företag</strong> enligt ÅRL. Ett företag är
                "mindre" om det <em>inte</em> överskrider mer än ett av följande gränsvärden under
                vart och ett av de två senaste räkenskapsåren:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6 border border-green-100">
                <ul className="space-y-4">
                  {[
                    { label: "Medelantal anställda", value: "högst 50" },
                    { label: "Balansomslutning", value: "högst 40 miljoner kr" },
                    { label: "Nettoomsättning", value: "högst 80 miljoner kr" },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">
                        <strong>{item.label}:</strong> {item.value}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-100 rounded-xl p-4">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span>
                    <strong>Över 95%</strong> av alla svenska aktiebolag uppfyller dessa krav.
                  </span>
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Vem får EJ använda K2?</h2>
              </div>

              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <ul className="space-y-3">
                  {[
                    "Publika aktiebolag",
                    "Moderföretag i större koncern",
                    "Företag som upprättar koncernredovisning",
                    "Bostadsrättsföreningar (från 2026)",
                    "Företag med kryptotillgångar (från 2026)",
                    "Företag med utländska filialer (från 2026)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <X className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-4 text-sm text-gray-500">
                Läs mer om{" "}
                <Link
                  to="/arsredovisning/guider/nya-regler-2026"
                  className="text-indigo-600 hover:underline font-medium"
                >
                  nya regler som gäller från 2026 →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* When to choose K3 */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">När ska du välja K3?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Även om du <em>får</em> använda K2 kan det finnas skäl att frivilligt välja K3
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Koncernredovisning",
                description:
                  "K2 tillåter inte koncernredovisning. Om du har eller planerar dotterbolag kan K3 vara att föredra.",
                icon: Building2,
              },
              {
                title: "Verkligt värde-värdering",
                description:
                  "Fastighetsbolag eller investeringsbolag kan ha nytta av verkligt värde-värdering som K3 tillåter.",
                icon: TrendingUp,
              },
              {
                title: "Externa intressenter",
                description:
                  "Banker, investerare eller potentiella köpare kan föredra K3 för dess mer detaljerade information.",
                icon: FileText,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Decision tree */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Beslutstrappa</h2>
            <p className="text-xl text-gray-600">Följ stegen för att avgöra vilket regelverk du ska välja</p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'Är du ett "mindre företag"?',
                detail: "(max 50 anställda, 40 MSEK balansomslutning, 80 MSEK omsättning)",
                noAnswer: "Du MÅSTE använda K3",
              },
              {
                question: "Behöver du upprätta koncernredovisning?",
                noAnswer: "Du MÅSTE använda K3",
              },
              {
                question: "Har du kryptotillgångar, utländska filialer eller är BRF (från 2026)?",
                noAnswer: "Du MÅSTE använda K3",
              },
              {
                question: "Kräver banker eller investerare K3?",
                noAnswer: "Överväg K3",
                highlight: "amber",
              },
            ].map((step, i) => (
              <div
                key={i}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 relative hover:border-indigo-200 transition-colors"
              >
                <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {i + 1}
                </div>
                <div className="ml-4">
                  <h3 className="font-semibold text-gray-900 mb-1">{step.question}</h3>
                  {step.detail && <p className="text-sm text-gray-500 mb-2">{step.detail}</p>}
                  <div
                    className={`inline-flex items-center gap-2 text-sm font-medium ${
                      step.highlight === "amber"
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  >
                    <span>Nej →</span>
                    <span>{step.noAnswer}</span>
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Annars: Välj K2</h3>
                  <p className="text-green-100">
                    K2 är enklare, billigare och räcker för de allra flesta småföretag.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-indigo-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Kvanten stödjer både K2 och K3
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Oavsett vilket regelverk du väljer hjälper Kvanten dig skapa en korrekt årsredovisning.
            Ladda upp din SIE-fil och vi identifierar automatiskt vilka krav som gäller.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/wizard"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-indigo-600 bg-white rounded-xl hover:bg-indigo-50 transition-colors shadow-lg"
            >
              Prova direkt
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/priser"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white border-2 border-white/30 rounded-xl hover:bg-white/10 transition-colors"
            >
              Se priser
            </Link>
          </div>
        </div>
      </section>

      {/* Related articles */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Läs mer</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "K2 Årsredovisning",
                href: "/arsredovisning/k2",
                description: "Detaljerad guide om K2-regelverket och vem som kan använda det",
              },
              {
                title: "K3 Årsredovisning",
                href: "/arsredovisning/k3",
                description: "Allt om K3, kassaflödesanalys och uppskjuten skatt",
              },
              {
                title: "Nya regler 2026",
                href: "/arsredovisning/guider/nya-regler-2026",
                description: "Kommande förändringar som påverkar K2-behörigheten",
              },
            ].map((article, i) => (
              <Link
                key={i}
                to={article.href}
                className="group block p-6 bg-gray-50 rounded-xl hover:bg-indigo-50 border border-transparent hover:border-indigo-200 transition-all"
              >
                <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-indigo-600">
                  {article.title}
                </h3>
                <p className="text-sm text-gray-600 mb-3">{article.description}</p>
                <span className="inline-flex items-center text-sm font-medium text-indigo-600">
                  Läs mer
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
